import React, { useState, useEffect, createContext } from 'react';

interface LocalizationContextType {
	getStr: (strCode: string, ...injections: string[]) => string;
	setStrings: (strings: { [key: string]: string }) => void;
	setUrls: (urls: { [key: string]: string }) => void;
	formatDateToLocal: (dateAdded: Date) => string;
	langCode: string;
	setLangCode: (langCode: string) => void;
}

const defaultContextValue: LocalizationContextType = {
	getStr: () => 'No strings have been loaded yet!', // Default implementation to avoid undefined errors
	setStrings: () => { console.log('default setStrings') },
	setUrls: () => { console.log('default setUrls') },
	formatDateToLocal: () => 'No date provided',
	langCode: '',
	setLangCode: () => { console.log('default setLangCode') }
};

const LocalizationContext = createContext<LocalizationContextType>(defaultContextValue);

interface LocalizationProviderProps {
	children: React.ReactNode;
	strings?: { [key: string]: string };
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children, strings: externalStrings }) => {
	const [strings, setStrings] = useState(externalStrings || {});
	const [urls, setUrls] = useState<{ [key: string]: string }>({});
	const [langCode, setLangCode] = useState('');

	// Update strings state when externalStrings prop changes
	useEffect(() => {
		if (externalStrings) {
			setStrings(externalStrings);
		}
	}, [externalStrings]);

	const getStr = (strCode: string, ...injections: string[]) => {
		const string = strings[strCode];
		if (!string) {
			return `NO STRING IMPORTED WITH VAR NAME "${strCode}"`;
		}

		return string.replace(/%%(\d+)%%/g, (match, number) => {
			const index = parseInt(number, 10) - 1;
			return injections[index] !== undefined ? injections[index] : match;
		});
	};

	const formatDateToLocal = (dateAdded: Date) => {
		const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
		return new Intl.DateTimeFormat(langCode, options).format(dateAdded);
	};

	return (
		<LocalizationContext.Provider value={{ getStr, setStrings, setUrls, formatDateToLocal, setLangCode, langCode }}>
			{children}
		</LocalizationContext.Provider>
	);
};

export default LocalizationContext;